import { useCallback } from 'react';
import { TaskStatus, RecurrenceInterval } from '../../types';
import type { Todo, TodoCreate } from '../../types';

interface UseTodoHandlersProps {
  allTodos: Todo[];
  selectedTodo: Todo | null;
  updateTodo: (params: { id: string; data: TodoCreate }, callbacks?: {
    onSuccess?: (data?: Todo) => void;
    onError?: (error: unknown) => void;
  }) => void;
  createTodo: (data: TodoCreate, callbacks?: {
    onSuccess?: (data: Todo) => void;
    onError?: (error: unknown) => void;
  }) => void;
  deleteTodo: (id: string, callbacks?: {
    onSuccess?: () => void;
    onError?: (error: unknown) => void;
  }) => void;
  showNotification: (type: 'success' | 'error' | 'info' | 'warning', message: string, actionText?: string, onAction?: () => void) => void;
  setSelectedTodo: (todo: Todo | null) => void;
}

export function useTodoHandlers({
  allTodos,
  selectedTodo,
  updateTodo,
  createTodo,
  deleteTodo,
  showNotification,
  setSelectedTodo,
}: UseTodoHandlersProps) {
  const handleUpdateTodo = useCallback((data: TodoCreate) => {
    if (!selectedTodo) return;

    const todoId = selectedTodo.id;

    updateTodo({ id: selectedTodo.id, data }, {
      onSuccess: (updatedTodo) => {
        // Use the server response if available, otherwise reconstruct from submitted data
        const todoToView = updatedTodo ?? { ...selectedTodo, ...data, id: todoId };
        showNotification('success', 'Task saved', 'View task', () => setSelectedTodo(todoToView as Todo));
        setSelectedTodo(null);
      },
      onError: (error: unknown) => {
        console.error('Failed to update todo:', error);
        showNotification('error', 'Failed to update task. Please try again.');
      }
    });
  }, [selectedTodo, updateTodo, showNotification, setSelectedTodo]);

  const handleAutoSaveTodo = useCallback((data: TodoCreate) => {
    if (!selectedTodo) return;

    updateTodo({ id: selectedTodo.id, data }, {
      onError: (error: unknown) => {
        console.error('Failed to auto-save todo:', error);
      }
    });
  }, [selectedTodo, updateTodo]);

  const handleCreateTodo = useCallback((todoData: TodoCreate) => {
    createTodo(todoData, {
      onSuccess: (newTodo) => {
        showNotification('success', 'Task created successfully!', 'View task', () => setSelectedTodo(newTodo));
      },
      onError: (error: unknown) => {
        console.error('Failed to create todo:', error);
        showNotification('error', 'Failed to create task. Please try again.');
      }
    });
  }, [createTodo, showNotification, setSelectedTodo]);

  const handleDeleteTodo = useCallback((todoId: string) => {
    deleteTodo(todoId, {
      onSuccess: () => {
        showNotification('success', 'Task deleted successfully!');
        setSelectedTodo(null);
      },
      onError: (error: unknown) => {
        console.error('Failed to delete todo:', error);
        showNotification('error', 'Failed to delete task. Please try again.');
      }
    });
  }, [deleteTodo, showNotification, setSelectedTodo]);

  const handleStatusChange = useCallback((todoId: string, status: TaskStatus) => {
    const todo = allTodos.find(t => t.id === todoId);
    if (!todo) return;

    const updateData: TodoCreate = {
      ...todo,
      status,
      parent_id: todo.parent_id || undefined,
      recurrence_interval: todo.recurrence_interval || undefined,
      team_id: todo.team_id || undefined,
      assigned_to_id: todo.assigned_to_id || undefined,
    };

    updateTodo({ id: todoId, data: updateData }, {
      onSuccess: () => {
        showNotification('success', `Task marked as ${status}`);
      },
      onError: (error: unknown) => {
        console.error('Failed to update todo status:', error);
        showNotification('error', 'Failed to update task status. Please try again.');
      }
    });
  }, [allTodos, updateTodo, showNotification]);

  const handleCloneTodo = useCallback(async (todoId: string) => {
    const todo = allTodos.find(t => t.id === todoId);
    if (!todo) return;

    const clonedData: TodoCreate = {
      title: `${todo.title} (Copy)`,
      description: todo.description,
      status: TaskStatus.NOT_STARTED,
      horizon: todo.horizon,
      deadline: todo.deadline,
      tags: todo.tags,
      parent_id: todo.parent_id,
      recurrence_interval: RecurrenceInterval.NEVER,
      team_id: todo.team_id,
      assigned_to_id: todo.assigned_to_id,
      next_step: todo.next_step,
      notes: todo.notes,
      source: todo.source,
      creator_id: todo.creator_id,
      estimated_time_minutes: todo.estimated_time_minutes
    };

    createTodo(clonedData, {
      onSuccess: () => {
        showNotification('success', 'Task cloned successfully!');
        setSelectedTodo(null);
      },
      onError: (error: unknown) => {
        console.error('Failed to clone todo:', error);
        showNotification('error', 'Failed to clone task. Please try again.');
      }
    });
  }, [allTodos, createTodo, showNotification, setSelectedTodo]);

  const handleTodoDateChange = useCallback((todoId: string, date: string | null, dateType: 'deadline') => {
    const todo = allTodos.find(t => t.id === todoId);
    if (!todo) return;

    const updateData: TodoCreate = {
      ...todo,
      [dateType]: date,
      parent_id: todo.parent_id || undefined,
      recurrence_interval: todo.recurrence_interval || undefined,
      team_id: todo.team_id || undefined,
      assigned_to_id: todo.assigned_to_id || undefined,
    };

    updateTodo({ id: todoId, data: updateData }, {
      onSuccess: () => {
        showNotification('success', 'Date updated successfully!');
      },
      onError: (error: unknown) => {
        console.error('Failed to update date:', error);
        showNotification('error', 'Failed to update date. Please try again.');
      }
    });
  }, [allTodos, updateTodo, showNotification]);

  const handleMakeChild = useCallback((childId: string, parentId: string) => {
    const childTodo = allTodos.find(t => t.id === childId);
    if (!childTodo) return;

    const updateData: TodoCreate = {
      ...childTodo,
      parent_id: parentId,
      recurrence_interval: childTodo.recurrence_interval || undefined,
      team_id: childTodo.team_id || undefined,
      assigned_to_id: childTodo.assigned_to_id || undefined,
    };

    updateTodo({ id: childId, data: updateData }, {
      onSuccess: () => {
        showNotification('success', 'Task relationship updated!');
      },
      onError: (error: unknown) => {
        console.error('Failed to update task relationship:', error);
        showNotification('error', 'Failed to update task relationship. Please try again.');
      }
    });
  }, [allTodos, updateTodo, showNotification]);

  return {
    handleUpdateTodo,
    handleAutoSaveTodo,
    handleCreateTodo,
    handleDeleteTodo,
    handleStatusChange,
    handleCloneTodo,
    handleTodoDateChange,
    handleMakeChild,
  };
}
